import type {
  OnboardingData,
  OnboardingStackParamList,
  PrimaryIntent,
  TreatmentIntent,
} from "./types";

export type OnboardingRouteEntry =
  | {
      name: "JourneyStage";
      params: OnboardingStackParamList["JourneyStage"];
    }
  | {
      name: "TreatmentType";
      params: OnboardingStackParamList["TreatmentType"];
    }
  | {
      name: Exclude<
        keyof OnboardingStackParamList,
        "JourneyStage" | "TreatmentType"
      >;
      params?: undefined;
    };

type PrimaryIntentRoute =
  | {
      name: "JourneyStage";
      params: OnboardingStackParamList["JourneyStage"];
    }
  | { name: "FamilyBuildingPath" }
  | { name: "HowAreYouHelping" }
  | { name: "Birthday" }
  | { name: "Sex" };

export function routeAfterPrimaryIntent(
  intent: PrimaryIntent,
): PrimaryIntentRoute {
  if (intent === "preserve") {
    return { name: "JourneyStage", params: { stage: intent } };
  }
  if (intent === "grow") return { name: "FamilyBuildingPath" };
  if (intent === "donate") {
    return { name: "HowAreYouHelping" };
  }
  // Even an exploratory visitor benefits from the same biological context
  // used to tailor testing and guidance. Keep this one question lightweight.
  return { name: "Sex" };
}

export function routeAfterFamilyBuildingPath(): {
  name: "Sex";
} {
  return { name: "Sex" };
}

type JourneySelectionRoute =
  | { name: "Birthday" }
  | { name: "WorkingWithSurrogate" }
  | { name: "Sex" }
  | {
      name: "TreatmentType";
      params: OnboardingStackParamList["TreatmentType"];
    };

export function routeAfterJourneySelection(
  stage: OnboardingStackParamList["JourneyStage"]["stage"],
  treatmentIntent: TreatmentIntent,
  fertilityStage: OnboardingData["fertilityStage"] = null,
): JourneySelectionRoute {
  if (
    treatmentIntent === "egg-freezing" ||
    treatmentIntent === "sperm-preserve"
  ) {
    // The selected gamete already establishes which biological context is
    // relevant. Do not make users answer a duplicate question.
    return { name: "Birthday" };
  }
  if (
    stage === "grow" &&
    (fertilityStage === "getting-ready" || fertilityStage === "in-treatment")
  ) {
    return { name: "TreatmentType", params: { stage: fertilityStage } };
  }
  if (stage === "grow") return { name: "Birthday" };
  return { name: "Sex" };
}

function nextRoute(
  current: OnboardingRouteEntry,
  data: Partial<OnboardingData>,
): OnboardingRouteEntry | null {
  switch (current.name) {
    case "IntroSplash":
      return { name: "WelcomeGoals" };
    case "WelcomeGoals":
      return data.primaryIntent
        ? routeAfterPrimaryIntent(data.primaryIntent)
        : null;
    case "FamilyBuildingPath":
      return routeAfterFamilyBuildingPath();
    case "JourneyStage":
      return data.treatmentIntent
        ? routeAfterJourneySelection(
            current.params.stage,
            data.treatmentIntent,
            data.fertilityStage,
          )
        : null;
    case "TreatmentType":
      return { name: "Birthday" };
    case "HowAreYouHelping":
      return { name: "Birthday" };
    case "WorkingWithSurrogate":
      return { name: "Sex" };
    case "Sex":
      if (data.primaryIntent === "grow") {
        return { name: "JourneyStage", params: { stage: "grow" } };
      }
      return { name: "Birthday" };
    case "Birthday":
      return { name: "Location" };
    case "Location":
      return { name: "CreateAccount" };
    default:
      return null;
  }
}

function isTargetRoute(
  route: OnboardingRouteEntry,
  target: OnboardingRouteEntry,
) {
  if (route.name !== target.name) return false;
  if (
    (route.name !== "JourneyStage" || target.name !== "JourneyStage") &&
    (route.name !== "TreatmentType" || target.name !== "TreatmentType")
  ) {
    return true;
  }
  return route.params.stage === target.params.stage;
}

/**
 * Replays the same branching rules used by the onboarding screens to build
 * the real stack that leads to a fixture target. Back can then pop actual
 * history instead of maintaining a separate route-to-predecessor table.
 */
export function buildOnboardingHistory(
  target: OnboardingRouteEntry,
  data: Partial<OnboardingData>,
): OnboardingRouteEntry[] | null {
  const routes: OnboardingRouteEntry[] = [{ name: "IntroSplash" }];
  const visited = new Set<string>();

  while (routes.length <= 12) {
    const current = routes[routes.length - 1]!;
    if (isTargetRoute(current, target)) return routes;

    const routeKey =
      current.name === "JourneyStage" || current.name === "TreatmentType"
        ? `${current.name}:${current.params.stage}`
        : current.name;
    if (visited.has(routeKey)) return null;
    visited.add(routeKey);

    const next = nextRoute(current, data);
    if (!next) return null;
    routes.push(next);
  }

  return null;
}
